import dayjs from 'dayjs'
import React, { FormEventHandler } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { AccountHeader } from '@mobi/component-library/Account'
import { AddressInput } from '@mobi/account/Common/Address/AddressInput'
import { Button, InfoBox, CustomerServiceEmail, Input } from '@mobi/component-library/Common/V2'
import { Checkbox } from '@mobi/component-library/Common/V2/Inputs/Checkbox'
import { spacing } from '@mobi/component-library/Theme/Common'
import { CountrySelectField, Nationality } from './CountrySelectField'
import {
  getEmailValidationMessage,
  getMobileValidationMessage,
  getNameValidationMessage,
  isEmailValid,
  isPhoneNumberValid,
  isValidName,
} from '../Utils/FormValidation'
import { PersonalDetailsDisclaimer } from './Message/PersonalDetailsDisclaimer'
import { post, SignUpUrl } from '@mobi/account/Utils/network'
import { routes } from '../Utils'
import {
  ManualAddress,
  searchStreetTypes,
  searchSuburbStatePostcodes,
} from '@mobi/account/Common/Address'
import { useAutoCompleteAddressList } from '@mobi/account/Areas/ContactDetails/Hooks'
import { useHistory, useHostContextFeature } from '@mobi/account/Hooks'
import { withValueMapping } from '../Data/Address'
import {
  VerificationResponse,
  type AccountSignUp,
  type DuplicateAccountAndInternetProspectResponse,
} from '@mobi/api-types/src/account/signup'
import {
  getFormattedDateOfBirth,
  validateDateOfBirthMustBe18OrOver,
} from '@mobi/component-library/Verification/helpers/dateOfBirth'
import { setFailedType, type State as SignUpState } from '../Store'
import { AddressMonikerId } from '@mobi/api-types'
import {
  BoldStyled,
  Caption,
  CheckboxLabel,
  FooterStyled,
  FormSection,
  Legend,
  PageLayout,
} from './PersonalDetails.styles'
import { serviceCenterEmail } from '@mobi/utils/email'

const AFFILIATE_ID = 'affiliateid'
const VENUE_ID = 'venueid'
const LOCATION_ID = 'locationid'

interface Props {
  initialState?: AccountSignUp
  onCompleted: (signUpInfo: SignUpState) => void
  setSignUpStarted: (signedUp: boolean) => void
}

type ValidAddress = AddressMonikerId | ManualAddress | undefined

export const PersonalDetails = ({ initialState, onCompleted, setSignUpStarted }: Props) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const isOptInMarketingEnabled = useHostContextFeature('signup-opt-in-marketing') ?? true
  const searchAddresses = useAutoCompleteAddressList()

  const initialDateOfBirth = initialState?.DateOfBirth
    ? dayjs(initialState.DateOfBirth).format('DD/MM/YYYY')
    : ''
  const [dateOfBirth, setDateOfBirth] = React.useState(initialDateOfBirth)
  const [firstName, setFirstName] = React.useState(initialState?.FirstName || '')
  const [middleName, setMiddleName] = React.useState(initialState?.MiddleName || '')
  const [lastName, setLastName] = React.useState(initialState?.Surname || '')
  const [emailAddress, setEmailAddress] = React.useState(initialState?.Email || '')
  const [mobile, setMobile] = React.useState(initialState?.MobileNumber || '')
  const [showPreviousAddress, setShowPreviousAddress] = React.useState(false)
  const [currentCountry, setCurrentCountry] = React.useState(Nationality.Australia)
  const [previousCountry, setPreviousCountry] = React.useState(Nationality.Australia)
  const [validCurrentAddress, setValidCurrentAddress] = React.useState(undefined as ValidAddress)
  const [validPreviousAddress, setValidPreviousAddress] = React.useState(undefined as ValidAddress)
  const isValidAddress =
    !!validCurrentAddress &&
    (!showPreviousAddress ||
      !!validPreviousAddress ||
      previousCountry === Nationality.International)
  const [hasAgreedToMarketing, setHasAgreedToMarketing] = React.useState(
    initialState?.MarketingOptIn || false
  )
  const [focusedField, setFocusedField] = React.useState<string | null>(null)
  const [validationMessages, setValidationMessages] = React.useState<Record<string, string>>({})

  // #region helper functions
  function useQuery() {
    const params = new URLSearchParams()
    new URLSearchParams(useLocation().search).forEach((value, name) => {
      params.append(name.toLowerCase(), value)
    })
    return params
  }

  const queries = useQuery()

  function getSignUpDetailsDto(): AccountSignUp {
    const dto = {
      DateOfBirth: validateDateOfBirthMustBe18OrOver(dateOfBirth, true),
      FirstName: firstName,
      Surname: lastName,
      MiddleName: middleName,
      Email: emailAddress,
      MobileNumber: mobile,
      MarketingOptIn: isOptInMarketingEnabled ? hasAgreedToMarketing : true,
      InteractiveSignup: true,
      AffiliateId: queries.get(AFFILIATE_ID) as string,
      LocationId: queries.get(LOCATION_ID) as string,
      VenueId: queries.get(VENUE_ID) as string,
    }
    return dto
  }

  function getSignUpAddressDto(): AccountSignUp {
    const isCurrentManual = isManualAddress(validCurrentAddress)
    const isPreviousManual = isManualAddress(validPreviousAddress)

    const buildAddress = (
      address: string | ManualAddress | undefined,
      isManual: boolean,
      country: string
    ) => {
      if (isManual) {
        const manualAddress = address as ManualAddress | undefined

        return {
          Moniker: undefined,
          Country: Nationality.Australia,
          UnitNumber: manualAddress?.unitNumber,
          StreetNumber: manualAddress?.streetNumber,
          StreetName: manualAddress?.streetName,
          StreetType: manualAddress?.streetType,
          SuburbStatePostCode: manualAddress?.suburbStateAndPostcode,
        }
      }

      return {
        Moniker: address as string | undefined,
        Country: country,
        UnitNumber: undefined,
        StreetNumber: undefined,
        StreetName: undefined,
        StreetType: undefined,
        SuburbStatePostCode: undefined,
      }
    }

    return {
      ...getSignUpDetailsDto(),
      IsInternational: currentCountry === Nationality.Australia,
      ResidentialAddress: buildAddress(validCurrentAddress, isCurrentManual, currentCountry),
      PreviousAddress: buildAddress(validPreviousAddress, isPreviousManual, previousCountry),
      InteractiveSignup: true,
    }
  }

  const createProspect = useMutation({
    mutationFn: (): Promise<DuplicateAccountAndInternetProspectResponse> =>
      post(SignUpUrl.CheckForDuplicateAccountAndCreateProspect, getSignUpDetailsDto()),
    onSuccess: data => {
      if (data.DuplicateExists) {
        dispatch(setFailedType('duplicateAccount'))
        history.push(routes.contactDetailsError)
      } else if (data.IsSuccessful) {
        verifyId.mutate({
          internetProspect: data,
        })
      }
    },
  })

  const verifyId = useMutation<
    VerificationResponse,
    Error,
    {
      internetProspect: DuplicateAccountAndInternetProspectResponse
    }
  >({
    mutationFn: (params): Promise<VerificationResponse> => {
      const signUp = {
        ...getSignUpAddressDto(),
        ApplicationReferenceNumber: params.internetProspect.ApplicationReferenceNumber,
      }
      return post(SignUpUrl.VerifyUser, signUp)
    },

    onSuccess: (verificationData, params) => {
      onCompleted({
        internetProspect: params.internetProspect,
        verification: verificationData,
        signUp: {
          ...getSignUpAddressDto(),
          ApplicationReferenceNumber: params.internetProspect.ApplicationReferenceNumber,
        },
      })
    },
  })

  function isManualAddress(address: ValidAddress): address is ManualAddress {
    return typeof address === 'object' && address !== null && 'streetName' in address
  }

  function getFormattedMobile(enteredText: string) {
    return enteredText.replace(/[\D]/gi, '')
  }

  const onSubmit: FormEventHandler = e => {
    e.preventDefault()
    createProspect.mutate()
  }

  const handleFocus = (fieldName: string) => {
    setFocusedField(fieldName)
  }

  const removeFocus = (fieldName: string) => {
    if (focusedField === fieldName) setFocusedField(null)
  }
  // #endregion

  const isOver18 = !!dateOfBirth && !validationMessages.dateOfBirth
  const hasValidEntries = Object.values(validationMessages).every(error => error === '')
  const isComplete =
    hasValidEntries &&
    dateOfBirth.length > 0 &&
    isValidName(firstName) &&
    isValidName(middleName, true) &&
    isValidName(lastName) &&
    isEmailValid(emailAddress) &&
    isPhoneNumberValid(mobile) &&
    isValidAddress
  const isBusy = createProspect.isPending || verifyId.isPending

  return (
    <form name='contact-details-form' onSubmit={onSubmit}>
      <PageLayout>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            color='secondary_grey'
            onClick={() => history.push('/account')}
            style={{ marginTop: spacing.md }}
            disabled={isBusy}
          >
            <span>
              Already have a <BoldStyled>TABtouch account?</BoldStyled>
            </span>
          </Button>
        </div>
        <AccountHeader
          alignItems='start'
          title='Join now and start betting!'
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your full name and DOB as it appears on your ID documents (including middle name
              if applicable)
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />
        <FormSection>
          <Legend>My details</Legend>
          <Input
            label='Date of Birth'
            name='dateOfBirth'
            placeholder='DD / MM / YYYY'
            required
            maxLength={10}
            value={dateOfBirth}
            onChange={e =>
              setDateOfBirth(
                getFormattedDateOfBirth(e.target.value, dateOfBirth, e.target.selectionStart)
              )
            }
            onFocus={() => {
              setSignUpStarted(true)
              handleFocus('dateOfBirth')
            }}
            onBlur={e => {
              removeFocus('dateOfBirth')
              setValidationMessages(prev => ({
                ...prev,
                dateOfBirth: validateDateOfBirthMustBe18OrOver(
                  getFormattedDateOfBirth(e.target.value, dateOfBirth, e.target.selectionStart)
                ),
              }))
            }}
            isInvalid={!!validationMessages.dateOfBirth}
            helpText={
              validationMessages.dateOfBirth || 'You must be aged 18 or over to wager in Australia'
            }
            isDisabled={isBusy}
          />
          {focusedField === 'dateOfBirth' && (
            <InfoBox color='blue' size='sm'>
              Your DOB must match your ID documents
            </InfoBox>
          )}

          <Input
            label='First name'
            name='firstName'
            placeholder='Details must match your ID'
            required
            maxLength={50}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            onFocus={() => handleFocus('firstName')}
            onBlur={e => {
              removeFocus('firstName')
              setValidationMessages(prev => ({
                ...prev,
                firstName: getNameValidationMessage(e.target.value),
              }))
            }}
            isInvalid={!!validationMessages.firstName}
            helpText={validationMessages.firstName}
            isDisabled={isBusy || !isOver18}
          />
          {focusedField === 'firstName' && (
            <InfoBox color='blue' size='sm'>
              Your name must match your ID documents
            </InfoBox>
          )}

          <Input
            label='Middle name'
            name='middleName'
            placeholder='Details must match your ID'
            maxLength={50}
            value={middleName}
            onChange={e => setMiddleName(e.target.value)}
            onFocus={() => handleFocus('middleName')}
            onBlur={e => {
              removeFocus('middleName')
              setValidationMessages(prev => ({
                ...prev,
                middleName: getNameValidationMessage(e.target.value, true),
              }))
            }}
            isInvalid={!!validationMessages.middleName}
            helpText={validationMessages.middleName}
            isDisabled={isBusy || !isOver18}
          />
          {focusedField === 'middleName' && (
            <InfoBox color='blue' size='sm'>
              Your name must match your ID documents
            </InfoBox>
          )}

          <Input
            label='Last name'
            name='lastName'
            placeholder='Details must match your ID'
            required
            maxLength={50}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            onFocus={() => handleFocus('lastName')}
            onBlur={e => {
              removeFocus('lastName')
              setValidationMessages(prev => ({
                ...prev,
                lastName: getNameValidationMessage(e.target.value),
              }))
            }}
            isInvalid={!!validationMessages.lastName}
            helpText={validationMessages.lastName}
            isDisabled={isBusy || !isOver18}
          />
          {focusedField === 'lastName' && (
            <InfoBox color='blue' size='sm'>
              Your name must match your ID documents
            </InfoBox>
          )}

          <Input
            type='tel'
            label='Mobile'
            name='mobile'
            placeholder='Enter a valid mobile number'
            required
            maxLength={10}
            value={mobile}
            onChange={e => setMobile(getFormattedMobile(e.target.value))}
            onBlur={e =>
              setValidationMessages(prev => ({
                ...prev,
                mobile: getMobileValidationMessage(e.target.value),
              }))
            }
            isInvalid={!!validationMessages.mobile}
            helpText={validationMessages.mobile}
            isDisabled={isBusy || !isOver18}
          />

          <Input
            type='email'
            label='Email'
            name='email'
            placeholder='Enter a valid email address'
            required
            maxLength={50}
            value={emailAddress}
            onChange={e => setEmailAddress(e.target.value)}
            onBlur={e =>
              setValidationMessages(prev => ({
                ...prev,
                email: getEmailValidationMessage(e.target.value),
              }))
            }
            isInvalid={!!validationMessages.email}
            helpText={validationMessages.email}
            isDisabled={isBusy || !isOver18}
          />

          {isOptInMarketingEnabled && (
            <div>
              <Checkbox
                id='sign-up-marketing-subscription'
                name='MarketingOptIn'
                checked={hasAgreedToMarketing || false}
                label={
                  <CheckboxLabel>
                    I consent to receive advertising and promotional material by email, post, SMS,
                    telephone and other direct means in line with TABtouch’s{' '}
                    <a
                      href='https://www.rwwa.com.au/privacy-policy/'
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      Privacy Policy
                    </a>
                  </CheckboxLabel>
                }
                data-tid-sign-up-marketing-subscription=''
                onChange={e => {
                  setHasAgreedToMarketing(e.target.checked)
                }}
                isDisabled={isBusy || !isOver18}
              />
            </div>
          )}
        </FormSection>
        <FormSection>
          <Legend>
            <div>Current Address</div>
          </Legend>
          <CountrySelectField
            value={currentCountry}
            onChange={value => {
              setCurrentCountry(value)
              setValidCurrentAddress(undefined)
            }}
            isDisabled={isBusy || !isOver18}
          />
          {currentCountry === 'Australia' && (
            <>
              <AddressInput
                prefix='current-address'
                fetchAddressSuggestionsCallback={searchAddresses}
                fetchStreetSuggestionsCallback={withValueMapping(searchStreetTypes)}
                fetchSuburbSuggestionsCallback={withValueMapping(searchSuburbStatePostcodes)}
                onValidationChange={change => {
                  if (change.isValid) setValidCurrentAddress(change.result)
                  else setValidCurrentAddress(undefined)
                }}
                isDisabled={isBusy || !isOver18}
              />
              <Checkbox
                id='sign-up-2-years'
                label={
                  <CheckboxLabel>Have you lived at this address less than 2 years?</CheckboxLabel>
                }
                name='TwoYears'
                checked={showPreviousAddress}
                data-tid-sign-up-has-been-2-years=''
                onChange={e => setShowPreviousAddress(e.target.checked)}
                isDisabled={isBusy || !isOver18}
              />
              <Caption>
                Only a physical address can be used for verification purposes. You can change your
                postal address (including PO box addresses) once your account has been opened.
              </Caption>
              {showPreviousAddress && (
                <>
                  <Legend>
                    <div>Previous Address</div>
                  </Legend>
                  <CountrySelectField
                    value={previousCountry}
                    onChange={value => {
                      setPreviousCountry(value)
                      setValidPreviousAddress(undefined)
                    }}
                    isDisabled={isBusy || !isOver18}
                  />
                  {previousCountry !== 'International' && (
                    <AddressInput
                      prefix='previous-address'
                      fetchAddressSuggestionsCallback={searchAddresses}
                      fetchStreetSuggestionsCallback={withValueMapping(searchStreetTypes)}
                      fetchSuburbSuggestionsCallback={withValueMapping(searchSuburbStatePostcodes)}
                      onValidationChange={change => {
                        if (change.isValid) setValidPreviousAddress(change.result)
                        else setValidPreviousAddress(undefined)
                      }}
                      isDisabled={isBusy || !isOver18}
                    />
                  )}
                </>
              )}
            </>
          )}
          {currentCountry !== 'Australia' && (
            <InfoBox color='grey' size='md'>
              <span>Don't have an Australian address?</span>
              <div>
                Our account opening process is a little different if you're living outside
                Australia. Please email us at <CustomerServiceEmail /> to assist you.
              </div>
            </InfoBox>
          )}
        </FormSection>
        <FooterStyled>
          {currentCountry === 'Australia' && (
            <>
              <Button
                type='submit'
                isFullWidth
                disabled={!isComplete || !isOver18}
                isLoading={isBusy}
              >
                Next
              </Button>
              <InfoBox color='grey' size='md' hideIcon={true}>
                <PersonalDetailsDisclaimer />
              </InfoBox>
            </>
          )}
          {currentCountry !== 'Australia' && (
            <Button
              isFullWidth
              onClick={() => {
                history.push(`mailto:${serviceCenterEmail}`)
              }}
            >
              Email now
            </Button>
          )}
        </FooterStyled>
      </PageLayout>
    </form>
  )
}
